import { useEffect, useRef, useState } from 'react';
import type { Ride } from '../../domain/ride';
import type { DraftControls } from '../../studio/StudioSettings';
import type { CinematicEditing } from '../../studio/use-cinematic-waypoints';
import { moveTimelineWaypoint } from './timeline-waypoint-edit';
import { deleteSceneKey } from './delete-scene-key';
import type { CinematicOverrides } from '../../../../renderer/src/cinematic-sequence';
import { browserUuid } from '../../domain/browser-crypto';
import { moveSceneProperty, patchSceneProperty } from './scene-property-edit';

export function useTimelineWaypointActions(ride: Ride, { draft, update }: DraftControls, editing: CinematicEditing,
  duration: number, pause: () => void) {
  const [error, setError] = useState('');
  const pendingSelection = useRef<{ id: string; property?: keyof CinematicOverrides | undefined } | null>(null);
  useEffect(() => {
    const selection = pendingSelection.current;
    if (selection) { pendingSelection.current = null; editing.select(selection.id, selection.property); }
  }, [draft.cinematicSequence]);
  const move = (id: string, seconds: number, property?: keyof CinematicOverrides) => {
    try {
      const points = ride.sourcePoints ?? ride.points;
      const moved = property ? moveSceneProperty(points, draft.cinematicSequence, id, property, seconds, duration, browserUuid())
        : { ...moveTimelineWaypoint(points, draft.cinematicSequence, id, seconds, duration), id };
      pause(); editing.follow(); setError(''); pendingSelection.current = { id: moved.id, property };
      update(current => ({ ...current, cinematicSequence: moved.sequence, progress: moved.progress }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'The keyframe could not be moved.'); }
  };
  const remove = (id: string, property?: keyof CinematicOverrides) => {
    pause(); editing.follow(); setError('');
    update(current => ({ ...current, cinematicSequence: deleteSceneKey(current.cinematicSequence, id, property) }));
  };
  const transition = (id: string, durationSeconds: number, property?: keyof CinematicOverrides) => {
    const key = draft.cinematicSequence.waypoints.find(item => item.id === id);
    if (!key) return;
    const timing = { ...key.transition, type: 'smooth' as const, durationSeconds: Math.max(.1, Math.min(30, durationSeconds)), entireInterval: false };
    try {
      const result = property ? patchSceneProperty(draft.cinematicSequence, id, property, timing, browserUuid())
        : { sequence: { ...draft.cinematicSequence, waypoints: draft.cinematicSequence.waypoints.map(item => item.id === id ? { ...item, transition: timing } : item) }, id };
      pause(); editing.follow(); setError(''); pendingSelection.current = { id: result.id, property };
      update(current => ({ ...current, cinematicSequence: result.sequence }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'The transition could not be saved.'); }
  };
  return { move, remove, transition, error };
}
